import React, { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import MenuIcon from '@material-ui/icons/Menu';
import RefreshIcon from '@material-ui/icons/Refresh';
import FilterIcon from "@material-ui/icons/FilterList";
import { LoadingOverlayWrapper } from 'components/layout';
import NameInput from './NameInput';
import GroupCard from './GroupCard';
import GroupManagerGrid from './GroupManagerGrid';
import {
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import {
  setSelectedModuleIsOpen,
  setSelectedModule,
  updateAssumptionModule,
  deleteAssumptionModule,
  setConfirmationModalState,
  setSelectedGroupId,
  setSelectedGroupIsOpen,
  setShowCustomShapes,
  fetchAssumptionModules,
  setSelectedModuleId,
} from 'store/actions';

const Module = ({
  classes,
  name,
  selectedModule,
  groupsAreLoading,
  selectedModuleIsDirty,
  hasSelectedGroup,
  selectedGroupId,
  selectedGroupIsOpen,
  setSelectedModuleIsOpen,
  updateAssumptionModule,
  deleteAssumptionModule,
  setConfirmationModalState,
  setSelectedGroupId,
  setSelectedGroupIsOpen,
  setSelectedModuleId,
  fetchAssumptionModules,
}) => {
  // == Constants =============================================

  const hasGroups = selectedModule && selectedModule.groups.length > 0;

  // == STATE =================================================

  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [filterActiveGroups, setFilterActiveGroups] = useState(false);

  const [groupManagerGridState, setGroupManagerGridState] = useState({
    open: false,
    shouldCollapseGrid: false,
  });

  // == EVENT HANDLERS ========================================

  const openMoreMenu = (e) => {
    setMoreMenuAnchorEl(e.currentTarget);
  };

  const closeMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };

  const handleRenameClick = (e) => {
    closeMoreMenu();
    setIsEditingName(true);
  };

  const handleNameSave = (name) => {
    updateAssumptionModule({ ...selectedModule, name: name });
    setIsEditingName(false);
  };

  const handleModuleDelete = () => {
    closeMoreMenu();
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => {
        closeConfirmationModal();
        deleteAssumptionModule(selectedModule.moduleId);
      },
      handleCancellation: () => {
        closeConfirmationModal();
      },
      titleText: 'Confirm',
      contentText: `Are you sure you want to delete ${selectedModule.name}?`,
    });
  };

  const handleBackToModules = () => {
    fetchAssumptionModules();
    setSelectedModuleIsOpen(false);
  };

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  };

  const handleGroupCardClick = (id) => {
    if (id !== selectedGroupId) {
      setSelectedGroupId(id);
    }

    setSelectedGroupIsOpen(true);
  };

  const fetchAssumptionGroups = () => {
    setSelectedModuleId(selectedModule.moduleId)
  }

  const toggleActiveGroups = () => {
    setFilterActiveGroups(!filterActiveGroups)
  }

  // == COMPONENT ================================================

  let groups = hasGroups ? selectedModule.groups : []

  if (filterActiveGroups) {
    groups = groups.filter(oo => oo.isActive)
  }

  return (
    <>
      <div
        className={classNames(
          classes.moduleContainer,
          hasSelectedGroup && selectedGroupIsOpen && classes.hiddenContainer
        )}
      >
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <Tooltip title='Back to all modules'>
              <span>
                <IconButton
                  onClick={() => handleBackToModules()}
                  size='small'
                  color='inherit'
                  classes={{
                    root: classes.iconButton,
                    disabled: classes.headerIconDisabled,
                  }}
                  style={{ margin: '0 4px' }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </span>
            </Tooltip>
            {isEditingName ? (
              <NameInput name={name} invertColor onSave={handleNameSave} />
            ) : (
              <>
                <Tooltip
                  title={
                    selectedModuleIsDirty ? 'Module has unsaved changes' : ''
                  }
                >
                  <Typography
                    variant='subtitle2'
                    className={classes.headerText}
                    color='inherit'
                  >
                    {name}
                  </Typography>
                </Tooltip>
                {selectedModuleIsDirty && (
                  <Typography
                    variant='subtitle2'
                    style={{ fontSize: '1.2rem' }}
                    color='inherit'
                  >
                    {'*'}
                  </Typography>
                )}
              </>
            )}
          </div>
          <div className={classes.headerRight}>
            <Tooltip title='Manage Groups'>
              <span>
                <IconButton
                  onClick={() =>
                    setGroupManagerGridState({
                      open: true,
                      shouldCollapseGrid: false,
                    })
                  }
                  size='small'
                  color='inherit'
                  classes={{
                    root: classes.iconButton,
                    disabled: classes.headerIconDisabled,
                  }}
                  style={{ margin: '0 4px' }}
                >
                  <MenuIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Add Group'>
              <span>
                <IconButton
                  onClick={() =>
                    setGroupManagerGridState({
                      open: true,
                      shouldCollapseGrid: true,
                    })
                  }
                  disabled={groupsAreLoading}
                  size='small'
                  color='inherit'
                  classes={{
                    root: classes.iconButton,
                    disabled: classes.headerIconDisabled,
                  }}
                >
                  <AddCircleIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Refresh Groups'>
              <IconButton
                onClick={fetchAssumptionGroups}
                size='small'
                color='inherit'
                classes={{
                  root: classes.iconButton,
                  disabled: classes.headerIconDisabled,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Toggle Active Groups'>
              <IconButton
                onClick={toggleActiveGroups}
                size='small'
                color='inherit'
                classes={{
                  root: classes.iconButton,
                  disabled: classes.headerIconDisabled,
                }}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              className={classes.iconButton}
              onClick={openMoreMenu}
              disabled={groupsAreLoading}
              size='small'
              color='inherit'
              classes={{
                root: classes.iconButton,
                disabled: classes.headerIconDisabled,
              }}
            >
              <MoreHorizIcon />
            </IconButton>
            <Menu
              anchorEl={moreMenuAnchorEl}
              open={Boolean(moreMenuAnchorEl)}
              onClose={closeMoreMenu}
              anchorOrigin={{
                horizontal: 'right',
                vertical: 'bottom',
              }}
            >
              <MenuItem onClick={handleRenameClick}>Rename Module</MenuItem>
              <MenuItem onClick={handleModuleDelete}>Delete Module</MenuItem>
            </Menu>
          </div>
        </div>
        <div className={classes.listContainer}>
          <LoadingOverlayWrapper
            className={classes.overlay}
            active={groupsAreLoading}
            message='Loading groups...'
          >
            {hasGroups ? (
              groups.map((group) => (
                <div key={group.groupId}>
                  {!group.deleted && (
                    <GroupCard group={group} module={selectedModule} onClick={handleGroupCardClick} />
                  )}
                </div>
              ))
            ) : (
              <div className={classes.noLayersDiv}>
                <Typography className={classes.noLayersText} variant='body2'>
                  Module contains no groups
                </Typography>
              </div>
            )}
          </LoadingOverlayWrapper>
        </div>
      </div>
      <GroupManagerGrid
        modalOpen={groupManagerGridState?.open}
        shouldCollapseGrid={groupManagerGridState?.shouldCollapseGrid}
        onClose={() => setGroupManagerGridState({ open: false })}
      />
    </>
  );
};

// == STYLES ==================================================
const styles = (theme) => ({
  moduleContainer: {
    flexShrink: 0,
    backgroundColor: '#f2f2f2',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  hiddenContainer: {
    height: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#3e4350',
    color: '#fff',
    marginBottom: 8,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    margin: '8px 0',
    minWidth: 0,
  },
  headerText: {
    fontSize: '1.2rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerRight: {
    marginRight: 4,
    display: 'flex',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'auto',
  },
  iconButton: {
    padding: '5px',
  },
  headerIconDisabled: {
    color: 'rgba(255, 255, 255, 0.2)!important',
  },
  noLayersDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 42,
    margin: '4px 8px',
  },
  noLayersText: {
    fontStyle: 'italic',
    color: '#a7a9ac',
    fontSize: '.9rem',
  },
  overlay: {
    height: '100%',
    overflow: 'auto',
  },
});

const mapStateToProps = ({ assumptionModulesState, assumptionGroupsState }) => {
  const { selectedModule, selectedModuleIsDirty } = assumptionModulesState;

  const { groupsAreLoading, selectedGroupId, selectedGroupIsOpen } =
    assumptionGroupsState;

  return {
    selectedModule: selectedModule,
    groupsAreLoading: groupsAreLoading,
    selectedModuleIsDirty: selectedModuleIsDirty,
    selectedGroupId: selectedGroupId,
    selectedGroupIsOpen: selectedGroupIsOpen,
    hasSelectedGroup:
      selectedModule &&
      selectedGroupId !== null &&
      selectedModule.groups.find(
        (group) => group.groupId === selectedGroupId
      ) != null,
  };
};

const mapDispatchToProps = {
  setSelectedModuleIsOpen,
  setSelectedModule,
  updateAssumptionModule,
  deleteAssumptionModule,
  setConfirmationModalState,
  setSelectedGroupId,
  setSelectedGroupIsOpen,
  setShowCustomShapes,
  fetchAssumptionModules,
  setSelectedModuleId,
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(Module);
